import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { PageHeader, Loading, EmptyState, inr } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { FolderKanban, CheckCircle2, Circle, Flag, ShieldCheck, X, Bell, FileText, Star } from "lucide-react";

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [amcForm, setAmcForm] = useState(null);
  const load = () => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/work-orders").then((r) => setWorkOrders(r.data)).catch(() => {});
    api.get("/amc-suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleMilestone = async (p, idx) => {
    const ms = p.milestones.map((m, i) => i === idx ? { ...m, done: !m.done } : m);
    await api.put(`/projects/${p.project_id}`, { milestones: ms }); load();
  };
  const complete = async (p) => {
    if (!window.confirm("Mark project as completed?")) return;
    await api.put(`/projects/${p.project_id}`, { status: "Completed" }); toast.success("Project completed — AMC suggested"); load();
  };
  const openAmc = (s) => setAmcForm({ suggestion_id: s.suggestion_id, category: s.category, vendor: s.vendor, start_date: s.suggested_start, scope: `AMC for ${s.title}` });
  const createAmc = async () => {
    await api.post("/tools/amcs", { ...amcForm, cost: +(amcForm.cost || 0) });
    await api.put(`/amc-suggestions/${amcForm.suggestion_id}/dismiss`, {});
    toast.success("AMC created"); setAmcForm(null); load();
  };
  const dismiss = async (id) => { await api.put(`/amc-suggestions/${id}/dismiss`, {}); load(); };

  const [rateFor, setRateFor] = useState(null);
  const [rateForm, setRateForm] = useState({ rating: 0, comment: "" });
  const openRate = (p) => { setRateFor(p); setRateForm({ rating: p.rating || 0, comment: p.rating_comment || "" }); };
  const submitRating = async () => {
    if (!rateForm.rating) { toast.error("Please select a star rating"); return; }
    try {
      await api.post(`/projects/${rateFor.project_id}/rating`, rateForm);
      toast.success("Thanks! Your vendor rating was saved."); setRateFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save rating"); }
  };

  if (!projects) return <Loading />;
  return (
    <div>
      <PageHeader title="Projects & Work Orders" subtitle="Track awarded work from execution to completion & warranty." />

      {suggestions.map((s) => (
        <div key={s.suggestion_id} className="bg-secondary/10 border border-secondary/30 rounded-xl p-4 mb-3 flex flex-wrap items-center justify-between gap-3" data-testid={`amc-suggestion-${s.suggestion_id}`}>
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-secondary shrink-0" />
            <div className="text-sm"><b>Set up an AMC for "{s.title}"</b><div className="text-muted-foreground text-xs">{s.category} · {s.vendor} · Warranty: {s.warranty || "—"}. Protect it after warranty ends.</div></div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full gap-1.5" onClick={() => openAmc(s)} data-testid={`amc-setup-${s.suggestion_id}`}><Bell className="w-3.5 h-3.5" /> Set up AMC</Button>
            <Button size="sm" variant="ghost" onClick={() => dismiss(s.suggestion_id)}><X className="w-4 h-4" /></Button>
          </div>
        </div>
      ))}
      {workOrders.length > 0 && (
        <div className="mb-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {workOrders.map((w) => (
            <div key={w.wo_id} className="bg-card border rounded-xl p-4" data-testid={`wo-${w.wo_id}`}>
              <div className="flex items-center justify-between"><span className="font-mono text-xs">{w.ref}</span><span className="text-xs rounded-full bg-primary/10 text-primary px-2 py-0.5">{w.status}</span></div>
              <div className="font-medium mt-1">{w.title}</div>
              <div className="text-xs text-muted-foreground">{w.vendor_name} · {inr(w.amount)}</div>
              <Button size="sm" variant="outline" className="rounded-full gap-1.5 mt-3 h-8" onClick={() => window.open(`${API}/pdf/work-order/${w.wo_id}`, "_blank")} data-testid={`wo-pdf-${w.wo_id}`}><FileText className="w-3.5 h-3.5" /> Work Order PDF</Button>
            </div>
          ))}
        </div>
      )}
      {projects.length === 0 ? <EmptyState icon={FolderKanban} title="No projects yet" subtitle="Award an RFQ quote from Requirements → Manage → RFQ & Quotes to create a project." /> : (
        <div className="space-y-4">
          {projects.map((p) => (
            <div key={p.project_id} className="bg-card border rounded-xl p-5" data-testid={`project-${p.project_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-muted-foreground font-mono">{p.ref}</div>
                  <h3 className="font-display font-bold">{p.title}</h3>
                  <div className="text-sm text-muted-foreground">{p.vendor_name} · {inr(p.amount)} {p.warranty && `· Warranty: ${p.warranty}`}</div>
                </div>
                <span className={`text-xs rounded-full px-2 py-1 ${p.status === "Completed" ? "bg-secondary/10 text-secondary" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1"><span>Progress</span><span>{p.progress}%</span></div>
                <Progress value={p.progress} className="h-2" />
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                {p.milestones.map((m, i) => (
                  <button key={i} onClick={() => toggleMilestone(p, i)} disabled={p.status === "Completed"} data-testid={`milestone-${p.project_id}-${i}`}
                    className="flex items-center gap-2 text-sm text-left hover:bg-muted rounded-lg px-2 py-1.5 disabled:opacity-60">
                    {m.done ? <CheckCircle2 className="w-4 h-4 text-secondary shrink-0" /> : <Circle className="w-4 h-4 text-muted-foreground shrink-0" />}
                    <span className={m.done ? "line-through text-muted-foreground" : ""}>{m.title}</span>
                  </button>
                ))}
              </div>
              {p.status !== "Completed" && (
                <Button onClick={() => complete(p)} className="rounded-full mt-4 gap-2" data-testid={`project-complete-${p.project_id}`}><Flag className="w-4 h-4" /> Mark Completed</Button>
              )}
              {p.status === "Completed" && p.vendor_id && (
                <div className="mt-4 flex flex-wrap items-center gap-3 border-t pt-3">
                  <span className="text-sm font-medium">Rate {p.vendor_name}:</span>
                  <div className="flex items-center gap-0.5" data-testid={`project-rating-${p.project_id}`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${p.rating >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
                    ))}
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => openRate(p)} data-testid={`project-rate-btn-${p.project_id}`}>
                    {p.rated ? "Edit Rating" : "Rate Vendor"}
                  </Button>
                </div>
              )}
              {p.completion_date && <div className="text-xs text-secondary mt-3">Completed on {p.completion_date}</div>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!amcForm} onOpenChange={(o) => !o && setAmcForm(null)}>
        <DialogContent data-testid="amc-from-warranty-dialog">
          <DialogHeader><DialogTitle className="font-display">Set up AMC</DialogTitle><DialogDescription>Create an annual maintenance contract from this completed project.</DialogDescription></DialogHeader>
          {amcForm && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm mb-1 block">Category</Label><Input value={amcForm.category} onChange={(e) => setAmcForm({ ...amcForm, category: e.target.value })} /></div>
              <div><Label className="text-sm mb-1 block">Vendor</Label><Input value={amcForm.vendor} onChange={(e) => setAmcForm({ ...amcForm, vendor: e.target.value })} /></div>
              <div><Label className="text-sm mb-1 block">Start Date</Label><Input type="date" value={amcForm.start_date || ""} onChange={(e) => setAmcForm({ ...amcForm, start_date: e.target.value })} /></div>
              <div><Label className="text-sm mb-1 block">Expiry Date</Label><Input type="date" value={amcForm.expiry_date || ""} onChange={(e) => setAmcForm({ ...amcForm, expiry_date: e.target.value })} data-testid="amc-warranty-expiry" /></div>
              <div><Label className="text-sm mb-1 block">Cost (₹)</Label><Input type="number" value={amcForm.cost || ""} onChange={(e) => setAmcForm({ ...amcForm, cost: e.target.value })} /></div>
              <div><Label className="text-sm mb-1 block">Visit Frequency</Label><Input value={amcForm.visit_frequency || ""} onChange={(e) => setAmcForm({ ...amcForm, visit_frequency: e.target.value })} placeholder="Quarterly" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={createAmc} className="rounded-full" data-testid="amc-warranty-create">Create AMC</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rateFor} onOpenChange={(o) => !o && setRateFor(null)}>
        <DialogContent data-testid="vendor-rating-dialog">
          <DialogHeader><DialogTitle className="font-display">Rate {rateFor?.vendor_name}</DialogTitle><DialogDescription>Share your experience so admins can rank preferred partners.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-2 block">Your rating</Label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setRateForm({ ...rateForm, rating: n })} data-testid={`rate-star-${n}`}>
                    <Star className={`w-8 h-8 transition-colors ${rateForm.rating >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40 hover:text-amber-300"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div><Label className="text-sm mb-1 block">Comment (optional)</Label>
              <Textarea value={rateForm.comment} onChange={(e) => setRateForm({ ...rateForm, comment: e.target.value })} placeholder="Quality of work, timeliness, professionalism…" data-testid="rate-comment" /></div>
          </div>
          <DialogFooter><Button onClick={submitRating} className="rounded-full" data-testid="rate-submit">Submit Rating</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
